import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { FolderIcon, Mail, MailOpen } from "lucide-react";
import { Skeleton } from "../../ui/skeleton";

import type { EmailFolder } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { EmailFolder };

type EmailFolderCardProps = EmailFolder & BaseEntityCardProps;

export function EmailFolderCardAvatar({
  className,
  style,
}: Partial<EmailFolderCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <FolderIcon className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function EmailFolderCard({
  platform,
  name,
  total_items,
  unread_items,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: EmailFolderCardProps) {
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <EmailFolderCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {/* Email count indicators */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {unread_items > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <span className="font-medium text-teal">
                          {unread_items}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{unread_items} unread emails</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1">
                      <MailOpen className="h-3 w-3" />
                      <span>{total_items}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{total_items} total emails</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function EmailFolderCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[74px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-lg" />
      <BaseEntityCardContent>
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-3 w-8" />
          </div>
        </div>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
